import { useState, useEffect } from 'react';
import { useStore } from '../store';
import { generateApi } from '../services/api';
import { imageApi } from '../services/imageApi';
import { Section, PAGE_LAYOUTS, PageLayout } from '../types';
import { IMAGE_PROVIDERS, ImageProvider } from '../types/images';
import {
  Sparkles,
  Edit2,
  Trash2,
  Check,
  X,
  Loader2,
  Paperclip,
  ArrowUp,
  ArrowDown,
  Plus,
  Image as ImageIcon,
  Settings2,
  Layout,
  BarChart2,
} from 'lucide-react';
import clsx from 'clsx';
import DataVizCreator from './visualizations/DataVizCreator';

interface SectionHeaderProps {
  section: Section;
  sectionNumber?: number;
  onGenerate?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDelete?: () => void;
  onAddAfter?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

// Layout preview SVG components
const LayoutPreview = ({ layout }: { layout: PageLayout }) => {
  const baseClasses = "w-full h-full";

  switch (layout) {
    case 'full-width':
      return (
        <svg viewBox="0 0 40 30" className={baseClasses}>
          <rect x="2" y="2" width="36" height="26" fill="#e5e7eb" rx="1" />
          <rect x="4" y="4" width="32" height="3" fill="#9ca3af" rx="0.5" />
          <rect x="4" y="9" width="32" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="13" width="28" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="17" width="32" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="21" width="24" height="2" fill="#d1d5db" rx="0.5" />
        </svg>
      );
    case 'image-right':
      return (
        <svg viewBox="0 0 40 30" className={baseClasses}>
          <rect x="2" y="2" width="36" height="26" fill="#e5e7eb" rx="1" />
          <rect x="4" y="4" width="18" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="8" width="16" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="12" width="18" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="16" width="14" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="24" y="4" width="12" height="20" fill="#3b82f6" rx="1" opacity="0.3" />
          <rect x="26" y="10" width="8" height="8" fill="#3b82f6" rx="0.5" opacity="0.6" />
        </svg>
      );
    case 'image-left':
      return (
        <svg viewBox="0 0 40 30" className={baseClasses}>
          <rect x="2" y="2" width="36" height="26" fill="#e5e7eb" rx="1" />
          <rect x="4" y="4" width="12" height="20" fill="#3b82f6" rx="1" opacity="0.3" />
          <rect x="6" y="10" width="8" height="8" fill="#3b82f6" rx="0.5" opacity="0.6" />
          <rect x="18" y="4" width="18" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="18" y="8" width="16" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="18" y="12" width="18" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="18" y="16" width="14" height="2" fill="#d1d5db" rx="0.5" />
        </svg>
      );
    case 'image-header':
      return (
        <svg viewBox="0 0 40 30" className={baseClasses}>
          <rect x="2" y="2" width="36" height="26" fill="#e5e7eb" rx="1" />
          <rect x="4" y="4" width="32" height="10" fill="#3b82f6" rx="1" opacity="0.3" />
          <rect x="12" y="7" width="16" height="4" fill="#3b82f6" rx="0.5" opacity="0.6" />
          <rect x="4" y="16" width="32" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="20" width="28" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="24" width="24" height="2" fill="#d1d5db" rx="0.5" />
        </svg>
      );
    case 'image-footer':
      return (
        <svg viewBox="0 0 40 30" className={baseClasses}>
          <rect x="2" y="2" width="36" height="26" fill="#e5e7eb" rx="1" />
          <rect x="4" y="4" width="32" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="8" width="28" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="12" width="24" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="16" width="32" height="10" fill="#3b82f6" rx="1" opacity="0.3" />
          <rect x="12" y="19" width="16" height="4" fill="#3b82f6" rx="0.5" opacity="0.6" />
        </svg>
      );
    case 'two-column':
      return (
        <svg viewBox="0 0 40 30" className={baseClasses}>
          <rect x="2" y="2" width="36" height="26" fill="#e5e7eb" rx="1" />
          <rect x="4" y="4" width="15" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="8" width="14" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="12" width="15" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="16" width="12" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="4" y="20" width="15" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="21" y="4" width="15" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="21" y="8" width="14" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="21" y="12" width="15" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="21" y="16" width="12" height="2" fill="#d1d5db" rx="0.5" />
          <rect x="21" y="20" width="15" height="2" fill="#d1d5db" rx="0.5" />
        </svg>
      );
    case 'overlay':
      return (
        <svg viewBox="0 0 40 30" className={baseClasses}>
          <rect x="2" y="2" width="36" height="26" fill="#e5e7eb" rx="1" />
          <rect x="4" y="4" width="32" height="22" fill="#3b82f6" rx="1" opacity="0.3" />
          <rect x="8" y="10" width="24" height="3" fill="white" rx="0.5" opacity="0.9" />
          <rect x="10" y="15" width="20" height="2" fill="white" rx="0.5" opacity="0.7" />
          <rect x="12" y="19" width="16" height="2" fill="white" rx="0.5" opacity="0.7" />
        </svg>
      );
    default:
      return null;
  }
};

/**
 * SectionHeader - Renders the header bar and settings panel for a section
 * This is separate from content so layouts only affect the content area
 */
export default function SectionHeader({
  section,
  sectionNumber,
  onGenerate,
  onMoveUp,
  onMoveDown,
  onDelete,
  onAddAfter,
  isFirst = false,
  isLast = false,
}: SectionHeaderProps) {
  const {
    updateSection,
    removeSection,
    applicantData,
    genericSections,
    localFiles,
    selectedDocuments,
    systemPrompt,
    nuanceSettings,
    referenceText,
    styleOptions,
    addNotification,
    addImageBlock,
    imageProvider,
    currentSessionId,
    hasUnsavedChanges,
    saveCurrentSession,
    documentConfig,
  } = useStore();

  const [editingTitle, setEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(section.title);
  const [showDocAttach, setShowDocAttach] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(!section.content);
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const [showDataVizCreator, setShowDataVizCreator] = useState(false);

  // Auto-collapse settings when generation starts
  useEffect(() => {
    if (section.isGenerating) {
      setIsSettingsOpen(false);
    }
  }, [section.isGenerating]);

  // Get image dimensions based on size setting
  const getImageDimensions = (size: 'square' | 'portrait' | 'landscape', provider: ImageProvider) => {
    const providerConfig = IMAGE_PROVIDERS[provider];
    const sizes = providerConfig?.supportedSizes || [];

    switch (size) {
      case 'portrait':
        return sizes.find(s => s.height > s.width) || { width: 1024, height: 1792 };
      case 'landscape':
        return sizes.find(s => s.width > s.height) || { width: 1792, height: 1024 };
      case 'square':
      default:
        return sizes.find(s => s.width === s.height) || { width: 1024, height: 1024 };
    }
  };

  // Auto-generate image prompt from content
  const generateImagePrompt = (content: string, title: string): string => {
    const firstSentence = content.split(/[.!?]/)[0]?.trim() || '';
    const context = firstSentence.length > 100 ? firstSentence.substring(0, 100) : firstSentence;
    return `Professional illustration for a section titled "${title}". Context: ${context}. Style: clean, modern, professional.`;
  };

  const handleGenerate = async () => {
    updateSection(section.id, { isGenerating: true });

    try {
      const genericFields: Record<string, Record<string, string>> = {};
      const titleCount: Record<string, number> = {};
      genericSections.forEach((gs) => {
        let sectionKey = gs.title;
        titleCount[gs.title] = (titleCount[gs.title] || 0) + 1;
        if (titleCount[gs.title] > 1) {
          sectionKey = `${gs.title} (${titleCount[gs.title]})`;
        }
        genericFields[sectionKey] = {};
        const labelCount: Record<string, number> = {};
        gs.fields.forEach((field) => {
          if (field.value) {
            let fieldKey = field.label;
            labelCount[field.label] = (labelCount[field.label] || 0) + 1;
            if (labelCount[field.label] > 1) {
              fieldKey = `${field.label} (${labelCount[field.label]})`;
            }
            genericFields[sectionKey][fieldKey] = field.value;
          }
        });
      });

      // Build document contents from section-specific documents
      const sectionDocContents = (section.documents || [])
        .filter((d: any) => d.content)
        .map((d: any) => ({ filename: d.filename || d.name || 'Document', content: d.content }));

      // Build document contents from global context documents
      const globalDocContents = (selectedDocuments || [])
        .filter((d: any) => d.content)
        .map((d: any) => ({ filename: d.filename || d.name || 'Document', content: d.content }));

      // Build document context from documentConfig
      let documentTypeContext = '';
      if (documentConfig) {
        const typeNames: Record<string, string> = {
          'childrens-book': "Children's Book",
          'novel': 'Novel',
          'short-story': 'Short Story',
          'poetry': 'Poetry Collection',
          'screenplay': 'Screenplay',
          'contract': 'Legal Contract',
          'agreement': 'Business Agreement',
          'letter': 'Formal Letter',
          'proposal': 'Business Proposal',
          'report': 'Report',
          'article': 'Article',
          'blog-post': 'Blog Post',
          'essay': 'Essay',
          'whitepaper': 'Whitepaper',
          'manual': 'User Manual',
          'guide': 'Guide',
        };
        const typeName = typeNames[documentConfig.documentType] || documentConfig.documentType;
        documentTypeContext = `\n\nDOCUMENT TYPE: ${typeName}${documentConfig.metadata?.title ? `\nTitle: ${documentConfig.metadata.title}` : ''}${documentConfig.metadata?.description ? `\nDescription: ${documentConfig.metadata.description}` : ''}\n\nWrite content appropriate for this document type.`;
      }

      const result = await generateApi.generate({
        section: {
          id: section.id,
          title: section.title,
          prompt: section.prompt + documentTypeContext,
          documents: section.documents,
          role: section.role,
        },
        applicantData: {
          ...applicantData,
          genericFields,
        },
        selectedDocuments: selectedDocuments || [],
        documentContents: [...globalDocContents, ...sectionDocContents],
        systemPrompt: systemPrompt || undefined,
        llmModel: applicantData.llmModel || undefined,
        referenceText: referenceText || undefined,
        styleOptions: referenceText ? styleOptions : undefined,
        nuanceSettings,
      });

      console.log('Generation result:', result);
      if (result.success) {
        console.log('Updating section with content:', result.content?.substring(0, 100));
        updateSection(section.id, { content: result.content });
        addNotification('Content generated', 'success');

        // Check if image generation is enabled
        if (section.imageConfig?.enabled) {
          updateSection(section.id, { isGenerating: false, isGeneratingImage: true });

          try {
            const imagePrompt = section.imageConfig.prompt?.trim()
              || generateImagePrompt(result.content, section.title);

            const dimensions = getImageDimensions(
              section.imageConfig.size || 'square',
              section.imageConfig.provider || 'openai'
            );

            const imageResult = await imageApi.generate({
              prompt: imagePrompt,
              provider: section.imageConfig.provider || 'openai',
              width: dimensions.width,
              height: dimensions.height,
            });

            console.log('Image generation result:', imageResult);
            if (imageResult.success && imageResult.image) {
              console.log('Adding image block with URL:', imageResult.image.url);
              // Add image locally first
              const tempBlock = {
                id: `temp-${Date.now()}`,
                sectionId: section.id,
                position: 'inline' as const, // Use inline to work with float layouts
                imageUrl: imageResult.image.url,
                width: 'medium' as const,
                alignment: 'center' as const,
                generatedPrompt: imagePrompt,
                provider: section.imageConfig.provider,
              };
              addImageBlock(section.id, tempBlock);
              console.log('Image block added to section:', section.id);
              addNotification('Image generated', 'success');

              // Try to save to backend if session exists
              if (currentSessionId) {
                try {
                  if (hasUnsavedChanges) {
                    await saveCurrentSession();
                  }
                  await imageApi.saveImageBlock(currentSessionId, {
                    sectionId: section.id,
                    position: 'after',
                    imageUrl: imageResult.image.url,
                    width: 'medium',
                    alignment: 'center',
                    generatedPrompt: imagePrompt,
                    provider: section.imageConfig.provider,
                  });
                } catch (saveErr) {
                  console.warn('Could not save image to session:', saveErr);
                }
              }
            }
          } catch (imgErr: any) {
            console.error('Image generation failed:', imgErr);
            const errorMsg = imgErr.message || 'Image generation failed';
            // Provide more specific error messages
            if (errorMsg.includes('API key') || errorMsg.includes('401') || errorMsg.includes('403')) {
              addNotification('Image API key not configured. Check Settings.', 'error');
            } else if (errorMsg.includes('timeout') || errorMsg.includes('timed out')) {
              addNotification('Image generation timed out. Try again.', 'error');
            } else {
              addNotification(errorMsg, 'error');
            }
          } finally {
            updateSection(section.id, { isGeneratingImage: false });
          }
        }
      } else {
        addNotification(result.error || 'Generation failed', 'error');
      }
    } catch (err: any) {
      console.error('Content generation error:', err);
      let errorMsg = 'Generation failed';

      if (err.response?.status === 504 || err.code === 'ECONNABORTED') {
        errorMsg = 'Request timed out - check if backend is running and API key is configured';
      } else if (err.response?.status === 400) {
        errorMsg = err.response?.data?.error || 'Missing API key - add one in Settings';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.message) {
        errorMsg = err.message;
      }

      addNotification(errorMsg, 'error');
    } finally {
      updateSection(section.id, { isGenerating: false, isGeneratingImage: false });
    }

    if (onGenerate) onGenerate();
  };

  const handleSaveTitle = () => {
    updateSection(section.id, { title: tempTitle });
    setEditingTitle(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    } else {
      removeSection(section.id);
    }
  };

  const handleAttachDocument = (doc: any) => {
    const alreadyAttached = section.documents.some((d) => d.id === doc.id);
    if (alreadyAttached) {
      handleDetachDocument(doc.id);
    } else {
      updateSection(section.id, { documents: [...section.documents, doc] });
      addNotification('Document attached', 'success');
    }
    setShowDocAttach(false);
  };

  const handleDetachDocument = (docId: string) => {
    const updatedDocs = section.documents.filter((d) => d.id !== docId);
    updateSection(section.id, { documents: updatedDocs });
    addNotification('Document detached', 'info');
  };

  const handleLayoutSelect = (layoutId: PageLayout) => {
    updateSection(section.id, { layout: layoutId });
    setShowLayoutPicker(false);
  };

  const currentLayout = PAGE_LAYOUTS.find(l => l.id === (section.layout || 'full-width'));

  return (
    <div className="section-header-v2 bg-white border-b border-gray-200 rounded-t-lg overflow-hidden">
      {/* Slim Header Bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {sectionNumber !== undefined && (
            <span className="w-6 h-6 flex items-center justify-center bg-accent text-white text-xs font-bold rounded-full flex-shrink-0">
              {sectionNumber}
            </span>
          )}

          {editingTitle ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                className="input text-sm flex-1 py-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
              />
              <button onClick={handleSaveTitle} className="p-1 text-green-600 hover:bg-green-50 rounded">
                <Check size={14} />
              </button>
              <button onClick={() => setEditingTitle(false)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0">
              <h3 className="font-medium text-gray-900 text-sm truncate">{section.title}</h3>
              <button
                onClick={() => {
                  setTempTitle(section.title);
                  setEditingTitle(true);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded flex-shrink-0"
              >
                <Edit2 size={12} />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Section Role */}
          <select
            value={section.role || ''}
            onChange={(e) => updateSection(section.id, { role: e.target.value || undefined })}
            className="text-[10px] text-gray-500 border border-gray-200 rounded px-1 py-0.5 bg-white hover:border-gray-300 max-w-[70px]"
            title="Section role"
          >
            <option value="">Role</option>
            <option value="introduction">Intro</option>
            <option value="body">Body</option>
            <option value="conclusion">Conclusion</option>
            <option value="transition">Transition</option>
            <option value="summary">Summary</option>
          </select>

          {/* Settings Toggle */}
          <button
            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            className={clsx(
              'p-1.5 rounded transition-colors',
              isSettingsOpen
                ? 'bg-accent text-white'
                : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
            )}
            title={isSettingsOpen ? 'Hide settings' : 'Show settings'}
          >
            <Settings2 size={14} />
          </button>

          {/* Move Up/Down */}
          <button
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move up"
          >
            <ArrowUp size={14} />
          </button>
          <button
            onClick={onMoveDown}
            disabled={isLast}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move down"
          >
            <ArrowDown size={14} />
          </button>

          {/* Generate */}
          <button
            onClick={handleGenerate}
            disabled={section.isGenerating || section.isGeneratingImage}
            className={clsx(
              'flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
              section.isGenerating || section.isGeneratingImage
                ? 'bg-gray-200 text-gray-400'
                : 'bg-accent text-white hover:bg-accent-dark'
            )}
          >
            {section.isGenerating ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                <span className="hidden sm:inline">Writing...</span>
              </>
            ) : section.isGeneratingImage ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                <span className="hidden sm:inline">Creating Image...</span>
              </>
            ) : (
              <>
                <Sparkles size={12} />
                <span className="hidden sm:inline">
                  {section.imageConfig?.enabled ? 'Generate All' : 'Generate'}
                </span>
              </>
            )}
          </button>


          {/* Add Section After */}
          {onAddAfter && (
            <button
              onClick={onAddAfter}
              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
              title="Add section after"
            >
              <Plus size={14} />
            </button>
          )}

          {/* Delete */}
          <button
            onClick={handleDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
            title="Delete section"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Collapsible Settings Panel */}
      <div
        className={clsx(
          'transition-all duration-300 ease-in-out bg-gray-50 border-t border-gray-100',
          isSettingsOpen ? 'max-h-[800px] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'
        )}
      >
        <div className="p-3 space-y-3">
          {/* AI Prompt */}
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase mb-1.5 block">
              AI Prompt
            </label>
            <textarea
              value={section.prompt}
              onChange={(e) => updateSection(section.id, { prompt: e.target.value })}
              className="input text-sm w-full"
              rows={2}
              placeholder="Enter AI instructions for this section..."
            />
          </div>

          {/* Image Generation */}
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <label className="text-xs font-medium text-gray-600 uppercase flex items-center gap-1.5">
              <ImageIcon size={10} />
              Generate Image
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={section.imageConfig?.enabled || false}
                onChange={(e) =>
                  updateSection(section.id, {
                    imageConfig: {
                      ...section.imageConfig,
                      enabled: e.target.checked,
                      provider: section.imageConfig?.provider || imageProvider || 'openai',
                      size: section.imageConfig?.size || 'square',
                    },
                  })
                }
                className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
              />
              <span className="text-xs text-gray-600">With content</span>
            </label>
          </div>

          {section.imageConfig?.enabled && (
            <div className="space-y-2 py-2">
              <div className="flex gap-2">
                <select
                  value={section.imageConfig.provider || 'openai'}
                  onChange={(e) =>
                    updateSection(section.id, {
                      imageConfig: {
                        ...section.imageConfig!,
                        provider: e.target.value as ImageProvider,
                      },
                    })
                  }
                  className="input text-xs flex-1 py-1"
                >
                  <option value="openai">DALL-E 3</option>
                  <option value="stability">Stability AI</option>
                  <option value="leonardo">Leonardo AI</option>
                  <option value="gemini">Google Imagen</option>
                </select>
                <select
                  value={section.imageConfig.size || 'square'}
                  onChange={(e) =>
                    updateSection(section.id, {
                      imageConfig: {
                        ...section.imageConfig!,
                        size: e.target.value as 'square' | 'portrait' | 'landscape',
                      },
                    })
                  }
                  className="input text-xs py-1 w-24"
                >
                  <option value="square">Square</option>
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>
              <textarea
                value={section.imageConfig.prompt || ''}
                onChange={(e) =>
                  updateSection(section.id, {
                    imageConfig: {
                      ...section.imageConfig!,
                      prompt: e.target.value,
                    },
                  })
                }
                className="input text-xs w-full"
                rows={2}
                placeholder="Image prompt (optional - auto-generates from content if empty)"
              />
            </div>
          )}

          {/* Data Visualization */}
          <div className="py-2 border-t border-gray-200">
            <label className="text-xs font-medium text-gray-600 uppercase flex items-center gap-1.5 mb-2">
              <BarChart2 size={10} />
              Data Visualization
            </label>
            <button
              onClick={() => setShowDataVizCreator(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-accent border border-accent/30 rounded-lg hover:bg-accent/5 transition-colors"
            >
              <Plus size={12} />
              Add Chart or Table
            </button>
          </div>

          {/* Section Documents */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-600 uppercase flex items-center gap-1">
                <Paperclip size={10} />
                Section Documents ({section.documents.length})
              </label>
              <button
                onClick={() => setShowDocAttach(!showDocAttach)}
                className="text-xs text-accent hover:text-accent-dark flex items-center gap-1 px-2 py-0.5 bg-accent/10 hover:bg-accent/20 rounded transition-colors"
              >
                <Plus size={10} />
                Attach
              </button>
            </div>

            {/* Attach dropdown */}
            {showDocAttach && (
              <div className="mb-2 border border-gray-200 rounded-md max-h-24 overflow-y-auto bg-white">
                {(localFiles || []).length === 0 && selectedDocuments.length === 0 ? (
                  <p className="text-xs text-gray-400 px-2 py-2 text-center">
                    No files available. Upload files in the Documents tab.
                  </p>
                ) : (
                  [...(localFiles || []), ...selectedDocuments]
                    .filter((file, index, self) => self.findIndex((f) => f.id === file.id) === index)
                    .map((doc) => {
                      const filename = 'filename' in doc ? doc.filename : ('name' in doc ? (doc as any).name : 'Document');
                      const isAttached = section.documents.some((d) => d.id === doc.id);
                      return (
                        <button
                          key={doc.id}
                          onClick={() => handleAttachDocument(doc)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 text-left border-b border-gray-100 last:border-b-0"
                        >
                          {isAttached ? (
                            <Check size={10} className="text-green-600 flex-shrink-0" />
                          ) : (
                            <Paperclip size={10} className="text-gray-400 flex-shrink-0" />
                          )}
                          <span className="truncate">{filename}</span>
                        </button>
                      );
                    })
                )}
              </div>
            )}

            {/* Document pills */}
            {section.documents.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {section.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-1 px-2 py-0.5 bg-accent/10 text-accent rounded text-xs"
                  >
                    <Paperclip size={8} />
                    <span className="max-w-[100px] truncate">
                      {'filename' in doc ? doc.filename : ('name' in doc ? doc.name : 'Document')}
                    </span>
                    <button
                      onClick={() => handleDetachDocument(doc.id)}
                      className="p-0.5 hover:bg-accent/20 rounded"
                    >
                      <X size={8} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-2 text-xs text-gray-400 border border-dashed border-gray-200 rounded bg-white">
                No documents attached
              </div>
            )}
          </div>

          {/* Page Layout - Visual Picker */}
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase mb-1.5 flex items-center gap-1">
              <Layout size={10} />
              Page Layout
            </label>

            {/* Current Layout Display / Toggle */}
            <button
              onClick={() => setShowLayoutPicker(!showLayoutPicker)}
              className="w-full flex items-center gap-3 p-2 border border-gray-200 rounded-lg bg-white hover:border-accent/50 transition-colors"
            >
              <div className="w-12 h-9 flex-shrink-0 border border-gray-200 rounded overflow-hidden">
                <LayoutPreview layout={section.layout || 'full-width'} />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-medium text-gray-800">{currentLayout?.name}</div>
                <div className="text-xs text-gray-500">{currentLayout?.description}</div>
              </div>
              <svg className={clsx("w-4 h-4 text-gray-400 transition-transform", showLayoutPicker && "rotate-180")} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Layout Options Grid */}
            {showLayoutPicker && (
              <div className="mt-2 grid grid-cols-2 gap-2 p-2 border border-gray-200 rounded-lg bg-white">
                {PAGE_LAYOUTS.map((layout) => (
                  <button
                    key={layout.id}
                    onClick={() => handleLayoutSelect(layout.id)}
                    className={clsx(
                      "flex items-center gap-2 p-2 rounded-lg border transition-all",
                      section.layout === layout.id || (!section.layout && layout.id === 'full-width')
                        ? "border-accent bg-accent/5 ring-1 ring-accent"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    )}
                  >
                    <div className="w-10 h-8 flex-shrink-0 border border-gray-200 rounded overflow-hidden bg-white">
                      <LayoutPreview layout={layout.id} />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-xs font-medium text-gray-700 truncate">{layout.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Data Visualization Creator Modal */}
      {showDataVizCreator && (
        <DataVizCreator
          sectionId={section.id}
          onClose={() => setShowDataVizCreator(false)}
        />
      )}
    </div>
  );
}
